import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  formatDetailDate,
  getCertificateStatusBadgeModifier,
  getCertificateStatusMessageId,
  resolveBadgeToneClass,
} from './utils';
import type { CertificateDetailHeaderProps } from './types';

import styles from './styles.module.scss';
import { CertificateStatusEnum } from '@share/common-types';

export const CertificateDetailHeader: React.FC<CertificateDetailHeaderProps> = ({
  name,
  certificateCode,
  status,
  purchasedAt,
  expiresAt,
}) => {
  const intl = useIntl();

  const purchasedDisplay = useMemo(
    (): string => formatDetailDate(purchasedAt, intl),
    [intl, purchasedAt],
  );
  const expiresDisplay = useMemo(
    (): string => formatDetailDate(expiresAt, intl),
    [intl, expiresAt],
  );

  const statusLabel = intl.formatMessage({ id: getCertificateStatusMessageId(status) });
  const badgeModifier = getCertificateStatusBadgeModifier(status);
  const badgeToneClass = resolveBadgeToneClass(badgeModifier);

  const purchasedLabel = intl.formatMessage({
    id: 'certificates.detail.certificate.purchasedLabel',
  });
  const expiresLabel = intl.formatMessage({ id: 'certificates.detail.certificate.expiresLabel' });

  return (
    <header className={styles.grid} aria-labelledby="certificate-detail-title">
      <h1 id="certificate-detail-title" className={styles.title}>
        {name}
      </h1>
      <div className={styles.badgeWrap}>
        <span className={`${styles.badge} ${badgeToneClass}`}>{statusLabel}</span>
      </div>
      <p className={styles.code}>{certificateCode}</p>
      <div className={styles.datesRow}>
        {!!purchasedDisplay && (
          <span>
            <span className={styles.dateLabel}>{purchasedLabel}</span>{' '}
            <span className={styles.dateValue}>{purchasedDisplay}</span>
          </span>
        )}
        {!!expiresDisplay && (
          <span>
            <span className={styles.dateLabel}>{expiresLabel}</span>{' '}
            <span className={styles.dateValue}>{expiresDisplay}</span>
          </span>
        )}
        {!expiresDisplay && status === CertificateStatusEnum.Pending && (
          <span>
            <span className={styles.dateLabel}>{expiresLabel}</span>{' '}
            <span className={styles.dateValue}>
              {intl.formatMessage({ id: 'certificates.detail.certificate.expiresLabel.pending' })}
            </span>
          </span>
        )}
      </div>
    </header>
  );
};
